<template>
  <section
    class="card"
    aria-label="Passwort-Resets"
  >
    <!-- Filter -->
    <div class="form">
      <div class="field">
        <label for="from">Datum von</label>
        <FlatPickr
          id="from"
          v-model="filters.from"
          :config="dateOnlyConfig"
          class="flatpickr-date"
          :placeholder="'TT.MM.JJJJ'"
        />
      </div>
      <div class="field">
        <label for="to">Datum bis</label>
        <FlatPickr
          id="to"
          v-model="filters.to"
          :config="dateOnlyConfig"
          class="flatpickr-date"
          :placeholder="'TT.MM.JJJJ'"
        />
      </div>
      <div class="field">
        <label for="status">Status</label>
        <select
          id="status"
          v-model="filters.status"
          class="input"
        >
          <option value="">
            Alle
          </option>
          <option value="email_sent">
            E-Mail gesendet
          </option>
          <option value="reset_done">
            Passwort gesetzt
          </option>
          <option value="expired">
            Abgelaufen
          </option>
          <option value="user_not_found">
            Unbekannte E-Mail-Adresse
          </option>
          <option value="mail_error">
            Versandfehler
          </option>
          <option value="rate_limited">
            Rate-Limit
          </option>
        </select>
      </div>
      <div class="field">
        <label for="q">Suche</label>
        <input
          id="q"
          v-model.trim="filters.q"
          class="input"
          type="text"
          :placeholder="'E-Mail oder Name'"
          @keyup.enter="reloadToFirstPage"
        >
      </div>
      <div class="field actions">
        <button
          class="btn btn-primary"
          :disabled="loading"
          @click="reloadToFirstPage"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          />
          Aktualisieren
        </button>
        <button
          class="btn"
          :disabled="loading"
          @click="resetFilters"
        >
          Reset
        </button>
      </div>
    </div>

    <!-- Hinweis -->
    <p class="help mt-4">
      Hinweis: Unbekannte E-Mail-Adressen werden protokolliert, es wird dabei keine Mail versendet.
    </p>

    <!-- KPIs -->
    <div class="kpis mt-12">
      <div class="kpi">
        <div class="kpi-label">
          Anfragen (gesamt)
        </div>
        <div class="kpi-value">
          {{ total }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi-label">
          E-Mails gesendet
        </div>
        <div class="kpi-value">
          {{ summaryEmailSent }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi-label">
          Passwort gesetzt
        </div>
        <div class="kpi-value">
          {{ summaryPasswordChanged }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi-label">
          Unbekannte E-Mail
        </div>
        <div class="kpi-value">
          {{ summaryUserNotFound }}
        </div>
      </div>
    </div>

    <!-- Tabelle -->
    <div class="table mt-12">
      <div class="thead">
        <div>Zeitpunkt</div>
        <div>Benutzer</div>
        <div>E-Mail</div>
        <div>Status</div>
        <div>Passwort gesetzt</div>
        <div class="text-right">
          Aktion
        </div>
      </div>
      <div
        v-if="loading"
        class="tbody"
      >
        <div class="row">
          <div
            class="cell"
            colspan="6"
          >
            Lädt …
          </div>
        </div>
      </div>
      <div
        v-else-if="error"
        class="tbody"
      >
        <div class="row">
          <div
            class="cell"
            colspan="6"
          >
            <p
              class="notice notice-danger"
              role="alert"
            >
              {{ error }}
            </p>
          </div>
        </div>
      </div>
      <div
        v-else
        class="tbody"
      >
        <div
          v-for="r in items"
          :key="r.id"
          class="row"
        >
          <div class="cell">
            {{ fmtDateTime(r.requested_at) }}
          </div>
          <div class="cell">
            <div
              v-if="r.user_id"
              class="user"
            >
              <span>{{ r.user_display || '—' }}</span>
            </div>
            <div
              v-else
              class="user-unknown"
            >
              <span>Unbekannt</span>
              <div class="muted small">
                Kein Benutzerkonto zugeordnet
              </div>
            </div>
          </div>
          <div class="cell">
            {{ r.email || '—' }}
          </div>
          <div class="cell">
            <span :class="['badge', badgeClass(r.status)]">{{ statusLabel(r.status) }}</span>
          </div>
          <div class="cell">
            {{ r.password_changed_at ? fmtDateTime(r.password_changed_at) : '—' }}
          </div>
          <div class="cell row-actions">
            <button
              class="btn icon-btn"
              @click="openDetails(r)"
            >
              <i
                class="pi pi-eye"
                aria-hidden="true"
                style="font-size:18px;color:var(--muted);"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div
      class="mt-12"
      style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
    >
      <span
        class="muted"
        style="margin-right:auto"
      >
        Gesamt: {{ total }}
      </span>

      <label
        class="muted"
        for="pwra-limit"
      >
        pro Seite:
      </label>
      <select
        id="pwra-limit"
        v-model.number="pageSize"
        :disabled="loading"
        @change="reloadToFirstPage"
      >
        <option
          v-for="n in [10,20,50,100]"
          :key="n"
          :value="n"
        >
          {{ n }}
        </option>
      </select>

      <button
        class="btn"
        :disabled="page <= 1 || loading"
        @click="goPrev"
      >
        Zurück
      </button>

      <span class="muted">
        Seite {{ page }} / {{ lastPage }}
      </span>

      <button
        class="btn"
        :disabled="page >= lastPage || loading"
        @click="goNext"
      >
        Weiter
      </button>
    </div>

    <!-- Details-Dialog -->
    <div
      v-if="dialog.open"
      class="overlay"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      @keydown.esc="closeDetails"
    >
      <div class="dialog">
        <div class="dialog-header">
          <h2 class="title">
            Details
          </h2>
          <button
            class="btn"
            @click="closeDetails"
          >
            Schließen
          </button>
        </div>
        <div
          v-if="dialog.item"
          class="dialog-body"
        >
          <div class="grid">
            <div>
              <div class="label">
                Angefordert
              </div>
              <div class="value">
                {{ fmtDateTime(dialog.item.requested_at) }}
              </div>
            </div>
            <div>
              <div class="label">
                Passwort gesetzt
              </div>
              <div class="value">
                {{
                  dialog.item.password_changed_at ? fmtDateTime(dialog.item.password_changed_at) : '—'
                }}
              </div>
            </div>
            <div>
              <div class="label">
                Status
              </div>
              <div class="value">
                <span
                  :class="['badge', badgeClass(dialog.item.status)]"
                >{{ statusLabel(dialog.item.status) }}</span>
              </div>
            </div>
            <div>
              <div class="label">
                E-Mail
              </div>
              <div class="value">
                {{ dialog.item.email || '—' }}
              </div>
            </div>
            <div>
              <div class="label">
                Benutzer:in
              </div>
              <div class="value">
                <template v-if="dialog.item.user_id">
                  {{ dialog.item.user_display || '—' }} <span
                    v-if="dialog.item.username"
                    class="muted small"
                  >({{ dialog.item.username }})</span>
                </template>
                <template v-else>
                  Unbekannt
                </template>
              </div>
            </div>
            <div>
              <div class="label">
                Versuche
              </div>
              <div class="value">
                <!-- z.B. "1. Versuch" statt nur "1" -->
                <span v-if="(dialog.item.attempt_count ?? 0) > 0">
                  {{ dialog.item.attempt_count }}. Versuch
                </span>
                <span v-else>
                  0 (noch kein Versuch mit diesem Link)
                </span>
              </div>
            </div>
            <div>
              <div class="label">
                IP
              </div>
              <div class="value">
                {{ dialog.item.request_ip || '—' }}
              </div>
            </div>
            <div class="span-2">
              <div class="label">
                User-Agent
              </div>
              <div class="value mono">
                {{ dialog.item.request_user_agent || '—' }}
              </div>
            </div>
            <div
              v-if="dialog.item.failure_reason"
              class="span-2"
            >
              <div class="label">
                Grund
              </div>
              <div class="value">
                {{ dialog.item.failure_reason }}
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-footer">
          <button
            class="btn btn-primary"
            @click="closeDetails"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import api from '@/services/api';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';

const today = new Date();
const dFrom = new Date(today);
dFrom.setDate(dFrom.getDate() - 14);

function toIsoDate(d) {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d2 = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d2}`;
}

const filters = ref({
  from: toIsoDate(dFrom),
  to: toIsoDate(today),
  status: '',
  q: ''
});

const dateOnlyConfig = {
  enableTime: false,
  dateFormat: 'Y-m-d',
  altInput: true,
  altFormat: 'd.m.Y',
  allowInput: true,
  clickOpens: true,
  disableMobile: true,
  locale: German
};

const loading = ref(false);
const error = ref('');
const items = ref([]);
const total = ref(0);
const page = ref(1);
const pageSize = ref(20);

// Aggregierte Summen vom Backend (filterbasiert, nicht paginiert)
const summary = ref({
  total: 0,
  email_sent: 0,
  reset_done: 0,
  user_not_found: 0,
  password_changed: 0
});

const lastPage = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)));
const countChanged = computed(() => items.value.filter(i => !!i.password_changed_at).length);

// Fallback-kompatible Wrapper für KPIs
const summaryEmailSent = computed(() =>
  summary.value.email_sent ?? items.value.filter(i => i.status === 'email_sent').length
);

const summaryPasswordChanged = computed(() =>
  summary.value.password_changed ?? countChanged.value
);

const summaryUserNotFound = computed(() =>
  summary.value.user_not_found ?? items.value.filter(i => i.status === 'user_not_found').length
);

function parseDbTimestampToUtcDate(v) {
  if (!v) return null;
  let iso = String(v).trim().replace(' ', 'T');

  // Mikrosekunden (6-stellig) → Millisekunden (3-stellig) kürzen
  iso = iso.replace(/(\.\d{3})\d+$/, '$1');

  // Wenn kein Offset/Z angegeben, als UTC interpretieren
  if (!/[zZ]|[+-]\d{2}:\d{2}$/.test(iso)) {
    iso += 'Z';
  }

  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function fmtDateTime(v) {
  const dt = parseDbTimestampToUtcDate(v);
  if (!dt) return '—';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(dt);
}

function statusLabel(s) {
  switch (s) {
    case 'email_sent':
      return 'E-Mail gesendet';
    case 'reset_done':
      return 'Passwort gesetzt';
    case 'expired':
      return 'Abgelaufen';
    case 'user_not_found':
      return 'Unbekannte E-Mail-Adresse';
    case 'mail_error':
      return 'Versandfehler';
    case 'rate_limited':
      return 'Rate-Limit';
    default:
      return s || '—';
  }
}

function badgeClass(s) {
  switch (s) {
    case 'email_sent':
      return 'info';
    case 'reset_done':
      return 'success';
    case 'expired':
      return 'warn';
    case 'user_not_found':
    case 'mail_error':
      return 'danger';
    case 'rate_limited':
      return 'warn';
    default:
      return '';
  }
}

async function load() {
  error.value = '';
  loading.value = true;
  try {
    const params = {
      from: filters.value.from || undefined,
      to: filters.value.to || undefined,
      status: filters.value.status || undefined,
      q: filters.value.q || undefined,
      page: page.value,
      pageSize: pageSize.value
    };
    const res = await api.get('/admin/password-resets/analytics', {params});
    const raw = res?.data || {};
    const list = Array.isArray(raw.items) ? raw.items : [];
    total.value = Number.isFinite(raw.total) ? raw.total : list.length;

    // Summary aus Backend übernehmen (falls vorhanden),
    // ansonsten aus der aktuellen Seite approximieren
    const rawSummary = raw.summary || {};
    summary.value = {
      total: Number.isFinite(rawSummary.total) ? rawSummary.total : total.value,
      email_sent: Number.isFinite(rawSummary.email_sent)
        ? rawSummary.email_sent
        : list.filter(r => (r.status ?? null) === 'email_sent').length,
      reset_done: Number.isFinite(rawSummary.reset_done)
        ? rawSummary.reset_done
        : list.filter(r => (r.status ?? null) === 'reset_done').length,
      user_not_found: Number.isFinite(rawSummary.user_not_found)
        ? rawSummary.user_not_found
        : list.filter(r => (r.status ?? null) === 'user_not_found').length,
      password_changed: Number.isFinite(rawSummary.password_changed)
        ? rawSummary.password_changed
        : list.filter(r => !!r.password_changed_at).length
    };

    items.value = list.map(r => ({
      id: r.id ?? r.reset_id ?? r.token_id ?? Math.random().toString(36).slice(2),
      requested_at: r.requested_at ?? r.requestedAt ?? r.created_at ?? r.createdAt ?? null,
      consumed_at: r.consumed_at ?? r.consumedAt ?? null,
      password_changed_at: r.password_changed_at ?? r.passwordChangedAt ?? null,
      email: r.email ?? null,
      user_id: r.user_id ?? r.userId ?? null,
      user_display: r.user_display ?? r.userDisplay ?? r.full_name ?? null,
      username: r.username ?? null,
      status: r.status ?? null,
      failure_reason: r.failure_reason ?? null,
      request_ip: r.request_ip ?? null,
      request_user_agent: r.request_user_agent ?? null,
      attempt_count: r.attempt_count ?? 0
    }));
  } catch (e) {
    error.value = 'Daten konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

function resetFilters() {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - 14);
  filters.value = {
    from: toIsoDate(start),
    to: toIsoDate(now),
    status: '',
    q: ''
  };
  page.value = 1;
  load();
}

function reloadToFirstPage() {
  page.value = 1;
  load();
}

function goPrev() {
  if (page.value > 1) {
    page.value -= 1;
    load();
  }
}

function goNext() {
  if (page.value < lastPage.value) {
    page.value += 1;
    load();
  }
}

watch(pageSize, () => reloadToFirstPage());

const dialog = ref({
  open: false,
  item: null
});

function openDetails(row) {
  dialog.value.item = row;
  dialog.value.open = true;
}

function closeDetails() {
  dialog.value.open = false;
  dialog.value.item = null;
}

onMounted(load);
</script>

<style scoped>
.card {
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  padding: 14px;
}

.form {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 220px));
  gap: 12px;
  align-items: end;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form .actions {
  justify-self: end;
  display: flex;
  flex-direction: row;
  gap: 8px;
}

label {
  font-size: 12px;
  color: var(--muted);
}

.input {
  height: 38px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
}

:deep(input.flatpickr-date) {
  height: 38px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
  width: 220px;
}

.mt-8 {
  margin-top: 8px;
}

.mt-12 {
  margin-top: 12px;
}

.muted {
  color: var(--muted);
}

.small {
  font-size: 12px;
}

/* KPIs */
.kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.kpi {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--surface);
}

.kpi-label {
  font-size: 12px;
  color: var(--muted);
}

.kpi-value {
  font-size: 18px;
  font-weight: 600;
}

/* Tabelle als kompaktes Grid */
.table {
  width: 100%;
}

.thead,
.row {
  display: grid;
  grid-template-columns: 150px 1fr 1fr 210px 130px 60px;
  align-items: center;
  gap: 8px;
}

.thead {
  font-size: 12px;
  color: var(--muted);
  padding: 8px 0;
  border-bottom: 1px solid var(--border);
  font-weight: 500;
}

.tbody .row {
  padding: 2px 0;
  border-bottom: 1px solid var(--border);
}

.cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
}

.user {
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.user-unknown {
  display: flex;
  flex-direction: column;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
}

.pagination .range {
  font-size: 12px;
  color: var(--muted);
}

.pagination .controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size {
  width: 80px;
}

/* Dialog Overlay */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.dialog {
  width: min(100%, 760px);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
}

.dialog-body {
  padding: 12px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid var(--border);
}

.title {
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
  margin: 0;
}

/* Details-Grid */
.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 5px;
}

.grid .span-2 {
  grid-column: span 2;
}

.label {
  font-size: 12px;
  color: var(--muted);
}

.value {
  font-size: 14px;
}

.value.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
}

.badge.info {
  background: color-mix(in srgb, var(--info) 10%, #fff 90%);
  border-color: color-mix(in srgb, var(--info) 40%, #fff 60%);
}

.badge.success {
  background: color-mix(in srgb, var(--success) 10%, #fff 90%);
  border-color: color-mix(in srgb, var(--success) 40%, #fff 60%);
}

.badge.warn {
  background: color-mix(in srgb, var(--warn) 10%, #fff 90%);
  border-color: color-mix(in srgb, var(--warn) 40%, #fff 60%);
}

.badge.danger {
  background: color-mix(in srgb, var(--danger) 10%, #fff 90%);
  border-color: color-mix(in srgb, var(--danger) 40%, #fff 60%);
}

.btn {
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
}

.btn-ghost {
  background: var(--surface);
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: transparent;
}

.btn-primary:hover {
  background: var(--primary-600);
}

.spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, .6);
  border-top-color: #fff;
  display: inline-block;
  margin-right: 6px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
  background: var(--surface);
}

.notice-danger {
  background: color-mix(in srgb, var(--danger) 10%, #fff 90%);
  border-color: color-mix(in srgb, var(--danger) 40%, #fff 60%);
  color: #7f1d1d;
}
</style>
